import AlertQueue from '../AlertQueue';
import giftColorThresholds from '../GiftThresholds';
import util from '../util';
import MessageWidget from './MessageWidget';
import UrlSound from '../UrlSound';
import PcmSound from '../PcmSound';

const BASE_CSS = `
.audio-resume-alert {
	position: absolute;
	top: 0; left: 0; right: 0;
	z-index: 9999;
	background: rgba(0,0,0,0.75);
	color: #fff;
	text-align: center;
	padding: 8px;
	font-size: 16px;
	cursor: pointer;
}
.widget.chat-widget {
	left: 0; right: 0; top: 0; bottom: 0;
	position: absolute;
	margin: 0; padding: 0;
	word-wrap: break-word;
	overflow-y: hidden;
	overflow-x: hidden;
}
.widget.chat-widget > .message {
	background: rgba(0, 0, 0, 0.25);
	color: white;
	margin: 4px;
	padding: 8px;
	border-radius: 4px;
	list-style-type: none;
	display: block;
}
/* Horizontal mode: one nowrap line of inline-block messages that scrolls sideways.
   The spacer doubles as a full-height strut so vertical-align on the messages
   positions the whole line at the top or the bottom of the widget. */
.widget.chat-widget.horizontal {
	white-space: nowrap;
	line-height: 0;
}
.widget.chat-widget.horizontal > .spacer {
	display: inline-block;
	height: 100%;
	vertical-align: top;
}
.widget.chat-widget.horizontal > .message {
	--chat-media-size: 1.75em;
	display: inline-block;
	white-space: nowrap;
	line-height: 0;
	vertical-align: top;
}
.widget.chat-widget.horizontal.align-bottom > .message {
	vertical-align: bottom;
}
.widget.chat-widget.horizontal > .message::before {
	content: '';
	display: inline-block;
	width: 0;
	height: var(--chat-media-size);
	vertical-align: middle;
}
.widget.chat-widget.horizontal > .message > * {
	line-height: normal;
	vertical-align: middle;
}
.widget.chat-widget > .message.subscription,
.widget.chat-widget > .message.subGiftReceive {
	background: #0f9d58;
}
/* One or two gift images float beside the text, same as a sticker/emote span would. The
   moment a 3rd shows up, :has() switches the whole block to its own centered row below
   the text instead — no JS-picked class needed, this is purely "how many .chatImage
   children does it actually have". Each further :has(:nth-child(N)) rule then shrinks
   that row a notch more: they're listed smallest-N-first, and since e.g. a 10-image block
   matches all of them simultaneously, the largest N present wins the cascade (equal
   specificity, later wins). */
.widget.chat-widget > .message > .chatImages { float: right; }
.widget.chat-widget > .message > .chatImages > .chatImage { max-width: 80px; max-height: 80px; }
/* :not(.horizontal): a :has() argument counts toward specificity same as a class does, so
   without this these would otherwise outrank (and permanently win over) horizontal mode's
   own .chatImages rules further down, regardless of source order. */
.widget.chat-widget:not(.horizontal) > .message > .chatImages:has(.chatImage:nth-child(3)) {
	float: none;
	clear: both;
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	align-items: center;
	gap: 2px;
	margin-top: 4px;
}
.widget.chat-widget:not(.horizontal) > .message > .chatImages:has(.chatImage:nth-child(3)) > .chatImage { max-width: 64px; max-height: 64px; }
.widget.chat-widget:not(.horizontal) > .message > .chatImages:has(.chatImage:nth-child(4)) > .chatImage { max-width: 56px; max-height: 56px; }
.widget.chat-widget:not(.horizontal) > .message > .chatImages:has(.chatImage:nth-child(5)) > .chatImage { max-width: 50px; max-height: 50px; }
.widget.chat-widget:not(.horizontal) > .message > .chatImages:has(.chatImage:nth-child(6)) > .chatImage { max-width: 44px; max-height: 44px; }
.widget.chat-widget:not(.horizontal) > .message > .chatImages:has(.chatImage:nth-child(7)) > .chatImage { max-width: 40px; max-height: 40px; }
.widget.chat-widget:not(.horizontal) > .message > .chatImages:has(.chatImage:nth-child(8)) > .chatImage { max-width: 36px; max-height: 36px; }
.widget.chat-widget:not(.horizontal) > .message > .chatImages:has(.chatImage:nth-child(9)) > .chatImage { max-width: 32px; max-height: 32px; }
.widget.chat-widget:not(.horizontal) > .message > .chatImages:has(.chatImage:nth-child(10)) > .chatImage { max-width: 28px; max-height: 28px; }
.widget.chat-widget > .message > .chatImages > .chatImages-overflow {
	font-weight: bold;
	opacity: 0.85;
	padding: 0 4px;
}
.widget.chat-widget > .message .sticker,
.widget.chat-widget > .message .image  { max-width: 80px; max-height: 80px; }
.widget.chat-widget > .message .emote  { vertical-align: middle; max-width: 32px; max-height: 32px; }
.widget.chat-widget > .message .platform { margin-right: 4px; }
.widget.chat-widget .message .avatar,
.widget.chat-widget .message .service-icon {
	width: 16px;
	height: 16px;
	object-fit: cover;
	margin-right: 4px;
	vertical-align: middle;
}
.widget.chat-widget .message .avatar {
	border-radius: 50%;
}
/* A stacked message wraps its text around the floated gift image; a horizontal one is a
   single nowrap line, so the same float simply lands at the right-hand end of it. Being a
   float it sits outside the line box and would set the message's height on its own, past
   the strut, so it is pinned to the strut's height and centres its images within that. */
.widget.chat-widget.horizontal > .message > .chatImages {
	margin-left: 6px;
	display: flex;
	align-items: center;
	height: var(--chat-media-size);
}
/* Emotes and stickers sit inside the message text rather than as direct children, so they
   need centring in their own right — left on the baseline they would hang below it and
   push the line box past the strut. */
.widget.chat-widget.horizontal > .message .sticker,
.widget.chat-widget.horizontal > .message .image,
.widget.chat-widget.horizontal > .message .emote,
.widget.chat-widget.horizontal > .message > .chatImages > .chatImage {
	vertical-align: middle;
	max-width: var(--chat-media-size);
	max-height: var(--chat-media-size);
}
.widget.chat-widget > .message .clearfix { clear: both; }
`;

export default class ChatWidget extends MessageWidget {
	constructor(opts) {
		super(opts);

		this.queue = new AlertQueue();
		this._currentItem = null;
		// Extra room held after a message is removed, shrunk a bit each frame so the
		// remaining messages slide into the gap instead of jumping.
		this._spacerPx = 0;
		this._scrollCarry = 0;
	}

	_build() {
		this._injectCSS('chatWidgetBaseCSS', BASE_CSS);
		this._injectGiftColorCSS();
		this._buildDOM();
		this._setupAudioMonitor();
	}

	_audioRequired() { return !!(this.settings.enableTTS || this.settings.enableEventSounds); }

	async init(initialData = {}) {
		this._startScrollAnimation();
		await super.init(initialData);
	}

	destroy() {
		this._stopScrollAnimation();
		super.destroy();
	}

	// ── Settings ──────────────────────────────────────────────────────────────

	applySettings(settings) {
		const s = settings ? { ...settings } : {};
		if(typeof s.ignoreUsers === 'string')
			s.ignoreUsers = s.ignoreUsers.split('\n').filter(Boolean);
		s.ignoreUsers = (s.ignoreUsers || []).map(a => a.toLowerCase());

		const oldOutTransition = this.settings.outTransition;
		const wasHorizontal = !!this.settings.horizontal;
		super.applySettings(s);
		this.container.classList.toggle('horizontal', !!this.settings.horizontal);
		this.container.classList.toggle('align-bottom', this.settings.alignment === 'bottom');
		if(wasHorizontal !== !!this.settings.horizontal) {
			// The spacer and the scroll offset live on the other axis now — start over.
			this._spacerPx = 0;
			this._scrollCarry = 0;
			this.spacer.style.width = '';
			this.spacer.style.height = '';
			this.container.scrollTop = 0;
			this.container.scrollLeft = 0;
		}
		this.queue.timeoutMs = s.loadTimeout || 5000;

		// remove old messages that had no out transition
		if(!oldOutTransition && settings.outTransition) {
			const messages = this.container.children;
			const ids = [];
			for(let i = 1; i < messages.length; i++) {
				if(messages[i].id)
				ids.push(messages[i].id);
			}
			for(let id of ids) this._removeMessage(id);
		}
	}


	_injectGiftColorCSS() {
		if(document.getElementById('chat-gift-color-thresholds-style')) return;
		const style = document.createElement('style');
		style.id = 'chat-gift-color-thresholds-style';
		style.textContent = giftColorThresholds.map(t => `
			.widget.chat-widget .gift.gift-${t.value} {
				color: ${util.getContrastingTextColor(t.color)};
				background-color: ${t.color};
			}
		`).join('');
		document.head.appendChild(style);
	}

	_buildDOM() {
		this.container.classList.add('widget', 'chat-widget');

		this._createAudioAlert();

		this.spacer = document.createElement('div');
		this.spacer.className = 'spacer';
		this.container.appendChild(this.spacer);

		this.queue.onSizeChange = size => this._applyChatSpeedup(size);
	}

	_applyChatSpeedup(size) {
		const minSpeedup = 5;
		const baseDuration = this.settings.duration || 500;

		if(size <= minSpeedup) {
			this.container.style.setProperty('--animate-duration', `${baseDuration}ms`);
			return;
		}

		const s = 1.0 + (size - minSpeedup) / 10.0;

		// Shorten animation duration proportionally (min 50ms)
		const newDuration = Math.max(50, Math.round(baseDuration / s));
		this.container.style.setProperty('--animate-duration', `${newDuration}ms`);

		if(!this._currentItem) return;
		try {
			if(s >= 2.0) {
				this._currentItem.sounds.length = 0; // drop all sounds (including any fallback)
			} else {
				for(const snd of this._currentItem.sounds) snd?.setPlaybackRate?.(s);
			}
		} catch(e) { /* ignore */ }
	}

	_generateCSS() {
		const css = `
			.widget.chat-widget {
				padding-top: ${this.settings.paddingTop || 0}px;
				padding-left: ${this.settings.paddingLeft || 0}px;
				padding-bottom: ${this.settings.paddingBottom || 0}px;
				padding-right: ${this.settings.paddingRight || 0}px;
			}
			.widget.chat-widget .message {
				${this.settings.textBg ? `background-color: ${this.settings.textBg};` : ''}
				${this.settings.textColor ? `color: ${this.settings.textColor};` : ''}
				${this.settings.fontFamily ? `font-family: ${this.settings.fontFamily};` : ''}
				${this.settings.fontSize > 0 ? `font-size: ${this.settings.fontSize}px;` : ''}
				${this.settings.letterSpacing > 0 ? `letter-spacing: ${this.settings.letterSpacing}px;` : ''}
				${this.settings.textAlign ? `text-align: ${this.settings.textAlign};` : ''}
				${this.settings.boldText ? `font-weight: bold;` : ''}
				${this.settings.italicText ? `font-style: italic;` : ''}
				${this.settings.underlineText || this.settings.strikethroughText ? `text-decoration: ${this.settings.underlineText ? 'underline' : ''} ${this.settings.strikethroughText ? 'line-through' : ''};` : ''}
				${this.settings.uppercaseText ? `text-transform: uppercase;` : ''}
				${this.settings.blurRadius > 0 || this.settings.spreadRadius > 0 ? `filter: ${util.generateSpreadShadowFilter(this.settings.spreadRadius, this.settings.blurRadius, this.settings.shadowColor)};` : ''}
			}
			.widget.chat-widget .message.isGift {
				${this.settings.giftBg ? `background-color: ${this.settings.giftBg};` : ''}
				${this.settings.giftColor ? `color: ${this.settings.giftColor};` : ''}
				-webkit-text-stroke-color: none;
				-webkit-text-stroke-width: 0;
				text-shadow: none;
			}
			.widget.chat-widget .message .avatar, .widget.chat-widget .message .service-icon {
				${this.settings.fontSize > 0 ? `width: ${this.settings.fontSize}px;` : ''}
				${this.settings.fontSize > 0 ? `height: ${this.settings.fontSize}px;` : ''}
			}
			.widget.chat-widget > .message .chatImage {
				${this.settings.fontSize > 0 ? `max-width: ${this.settings.fontSize * 3}px;` : ''}
				${this.settings.fontSize > 0 ? `max-height: ${this.settings.fontSize * 3}px;` : ''}
			}
			${this.settings.enableSpeakingHighlight && this.settings.speakingOutlineColor ? `
			.widget.chat-widget > .message.speaking {
				filter: ${util.generateSpreadShadowFilter(this.settings.spreadRadius || 2, this.settings.blurRadius || 10, this.settings.speakingOutlineColor)};
			}` : ''}
			${this.settings.enableLoadingHighlight && this.settings.loadingOutlineColor ? `
			.widget.chat-widget > .message.loading {
				filter: ${util.generateSpreadShadowFilter(this.settings.spreadRadius || 2, this.settings.blurRadius || 10, this.settings.loadingOutlineColor)};
			}` : ''}
			:root { --animate-duration: ${this.settings.duration || 500}ms; }
			${this.settings.extraCSS || ''}
		`;
		this._setCSS('chatWidgetStyle', css);
		this._updateFontLink();
	}

	// ── Chat message handling ─────────────────────────────────────────────────

	_onChat(data) {
		if(data.type === 'delete') {
			for(const msgId of data.ids) {
				const itemId = `${data.serviceId}-${msgId}`;
				const removed = this.queue.cancel(it => it.prepared.id === itemId);
				for(const item of removed) item.sounds?.forEach(s => s.stop());
				this._removeMessage(itemId, true);
			}
			return;
		} else if(data.type == 'clear') {
			const removed = this.queue.cancel(it => it.prepared.serviceId === data.serviceId && it.prepared.channelToken == data.target?.channelToken && it.prepared.chatToken == data.target?.chatToken);
			for(const item of removed) item.sounds?.forEach(s => s.stop());
			for(let i = 0; i < this.container.childNodes.length; i++) {
				const child = this.container.childNodes[i];
				if(child.dataset.serviceId == data.serviceId && child.dataset.channelToken == data.target?.channelToken && child.dataset.chatToken == data.target?.chatToken)
					this.container.childNodes[i--].remove();
			}

			return;
		}

		if(this.settings.excludedChannels?.includes(`${data.serviceId}:${data.target?.channelToken}`)) return;

		const displayName = data.sender?.displayName || data.sender?.username || data.sender?.token || '';
		if(displayName && (this.settings.ignoreUsers || []).indexOf(displayName.toLowerCase()) >= 0) return;

		if(!this.settings.showBotMessages && data.sender?.isSelf) return;
		if(!this.settings.showUserCommands && data.isCommand) return;

		const prepared = util.prepareMessage(data, {
			id: true, serviceId: true, serviceName: true, serviceIconUrl: true,
			channelToken: true, chatToken: true,
			avatarUrl: true, displayName: true, spansHtml: true, giftString: true,
			eventString: true, classList: true, giftColorThreshold: true,
			sounds: true,
			group: true, groupId: true, images: true, consoleLog: true,
		}, {
			giftTypes: this.giftTypes,
			eventTypes: this.eventTypes,
			serviceIcons: this.serviceIcons,
			user: this.userData,
			giftColorThresholds,
			giftMediaThresholds: this.giftThresholds,
			textReplacements: this.textReplacements,
		});

		console.log.apply(null, prepared.consoleLog);

		if(prepared.group) {
			const allMessages = Array.from(this.container.children);
			const lastInGroup = allMessages.findLast(el => el.dataset.groupId === prepared.groupId);
			if(lastInGroup) {
				data.multiplier ||= 1;
				data.multiplier += parseInt(lastInGroup.dataset.multiplier || '1');
				lastInGroup.dataset.multiplier = String(data.multiplier);
				const giftElem = lastInGroup.querySelector('.gift-string');
				if(giftElem) giftElem.textContent = ` ${util.generateGiftString(data, this.giftTypes?.[data.serviceId]?.[data.giftType]).displayString}`;

				if(prepared.images?.length)
					util.placeChatImages(lastInGroup, prepared.images.filter(img => img?.url).map(img => img.url));

				return;
			}
		}



		for(let i = 0; i < 10; i++) {
			if(!document.getElementById(prepared.id)) break;
			prepared.id = util.generateRandomString(20);
		}
		prepared.classList.push('animate__animated');

		const maxMessages = 50;
		const messages = this.container.children;
		while(messages.length > maxMessages) {
			const toRemove = messages[1]; // messages[0] is the spacer
			const next = messages[2];
			this._growSpacer(this.settings.horizontal
				? (next ? next.offsetLeft - toRemove.offsetLeft : toRemove.offsetWidth)
				: (next ? next.offsetTop - toRemove.offsetTop : toRemove.offsetHeight));
			toRemove.remove();
		}

		const msgElem = this._createMessageElement(prepared);
		if(this.settings.inTransition)
			msgElem.classList.add(`animate__${this.settings.inTransition}`);
		if(prepared.groupId)
			msgElem.dataset.multiplier = String(data.multiplier||1);
		this.container.appendChild(msgElem);

		const item = { prepared, elem: msgElem };

		this.queue.append({
			data:    item,
			preload: signal => this._preloadChat(item, signal),
			play:    signal => this._processChat(item, signal),
		});
	}

	async _preloadChat(item, signal) {
		if(this.queue.pendingItems.length >= 15) return;
		if(!this.settings.enableTTS && !this.settings.enableEventSounds) return;

		item.elem.classList.add('loading');
		try {
			const loadSounds = [];
			const loadSoundsRecursive = async sounds => {
				try {
					if(Array.isArray(sounds)) {
						for(let sound of sounds) {
							if(await loadSoundsRecursive(sound)) return true;
						}
						throw new Error('Could not load a sound');
					}

					if(sounds.type == 'any') {
						let loaded = 0;
						for(let sound of sounds.items) {
							if(await loadSoundsRecursive(sound)) loaded++;
						}
						if(!loaded) throw new Error('Could not load any sound');
						return true;
					}

					let category = 'event';
					if(sounds.type == 'tts') {
						if(!this.settings.enableTTS) return false;
						category = 'tts';
					} else if(sounds.type == 'song') {
						throw new Error(`Unknown sound type ${sounds.type}`);
					} else {
						if(!this.settings.enableEventSounds) return false;
					}

					let p;
					if(sounds.url) p = new UrlSound(this.soundManager, this.soundCache, sounds.proxy ? `${this.baseUrl}/proxy/${encodeURIComponent(sounds.url)}` : sounds.url);
					else if(sounds.type == 'tts') {
						p = new PcmSound(this.soundManager, this.soundCache, signal => this.voiceApi.synthesizeSpeech(sounds.speak, sounds.provider, sounds.voice, sounds.rate, sounds.pitch, sounds.style, sounds.role, signal));
					} else if(sounds.type == 'mml') {
						p = new PcmSound(this.soundManager, this.soundCache, signal => this.mmlApi.synthesizeMusic(sounds.mml, sounds.instrument, signal));
					}
					p.category = category;
					await p.load(signal);
					loadSounds.push(p);
					return true;
				} catch(e) {
					console.error('Could not load sound', sounds, e);
					return false;
				}
			};
			try {
				await loadSoundsRecursive(item.prepared.sounds);
				item.sounds = loadSounds;
			} catch(e) {
				console.error('Could not load any sounds', e);
			}
		} finally {
			item.elem.classList.remove('loading');
		}
	}

	async _processChat(item, signal) {
		item.elem.classList.add('speaking');
		this._currentItem = item;
		this._applyChatSpeedup(this.queue.pendingItems.length);
		try {
			if(this.queue.pendingItems.length >= 15) throw 'queue full';
			if(!this.settings.enableTTS && !this.settings.enableEventSounds) throw 'sounds disabled';
			if(!item.sounds) throw 'no sounds';
			for(const s of item?.sounds) {
				if(s.category == 'tts' && !this.settings.enableTTS) continue;
				if(s.category == 'event' && !this.settings.enableEventSounds) continue;
				try { await s.play(signal); } catch(e) {
					if(!(e instanceof DOMException && e.name === 'AbortError')) console.error('Could not play sound', s, e);
				}
				if(signal?.aborted) break;
			}
		} catch(e) {
			//
		} finally {
			this._currentItem = null;
		}
		item.elem.classList.remove('speaking');

		if(this.settings.outTransition)
			setTimeout(() => this._removeMessage(item.prepared.id), this.settings.linger || 8000);
	}

	// ── DOM helpers ───────────────────────────────────────────────────────────

	_createMessageElement(prepared) {
		const div = document.createElement('div');
		div.id = prepared.id;
		if(prepared.groupId) div.dataset.groupId = prepared.groupId;
		div.dataset.multiplier = '1';
		for(const cls of (prepared.classList || [])) div.classList.add(cls);
		div.dataset.serviceId = prepared.serviceId;
		div.dataset.channelToken = prepared.channelToken;
		div.dataset.chatToken = prepared.chatToken;
		div.dataset.chatterToken = prepared.chatterToken;

		if(prepared.serviceIconUrl) {
			const icon = document.createElement('img');
			icon.src = prepared.serviceIconUrl;
			icon.alt = prepared.serviceName || '';
			icon.className = 'service-icon';
			div.appendChild(icon);
		} else if(prepared.serviceName) {
			const small = document.createElement('small');
			small.className = 'platform';
			small.textContent = prepared.serviceName;
			div.appendChild(small);
		}

		if(prepared.avatarUrl) {
			const avatar = document.createElement('img');
			avatar.src = prepared.avatarUrl;
			avatar.alt = '';
			avatar.className = 'avatar';
			div.appendChild(avatar);
		}

		const name = document.createElement('b');
		name.textContent = prepared.displayName;
		div.appendChild(name);

		if(prepared.giftString) {
			const giftElem = document.createElement('b');
			giftElem.className = 'gift-string';
			giftElem.textContent = ` ${prepared.giftString}`;
			div.appendChild(giftElem);
		}

		if(prepared.eventString) {
			const eventElem = document.createElement('span');
			eventElem.textContent = ` ${prepared.eventString}`;
			div.appendChild(eventElem);
		}

		if(prepared.spansHtml) {
			const spansElem = document.createElement('span');
			spansElem.innerHTML = `: ${prepared.spansHtml}`;
			div.appendChild(spansElem);
		}

		const clearfix = document.createElement('div');
		clearfix.className = 'clearfix';
		div.appendChild(clearfix);

		if(prepared.images?.length)
			util.placeChatImages(div, prepared.images.filter(img => img?.url).map(img => img.url));

		return div;
	}

	_removeMessage(id, force) {
		const el = document.getElementById(id);
		if(!el) return;
		// Whatever room the message gave up goes to the spacer, on the scrolling axis
		const gap = this.settings.horizontal ? el.offsetWidth : el.offsetHeight;
		if(this.settings.outTransition && !force) {
			el.classList.remove(`animate__${this.settings.inTransition}`);
			// Snapshot layout before taking element out of flow
			const top = el.offsetTop;
			const left = el.offsetLeft;
			const width = el.offsetWidth;
			const height = el.offsetHeight;
			// Add to spacer now — it starts shrinking while the animation plays
			this._growSpacer(gap);
			// Pin element in place visually while it plays its exit animation
			el.style.position = 'absolute';
			el.style.top = top + 'px';
			el.style.left = left + 'px';
			el.style.width = width + 'px';
			el.style.height = height + 'px';
			el.classList.add(`animate__${this.settings.outTransition}`);
			const fallback = setTimeout(() => el.remove(), (this.settings.duration || 500) + 500);
			el.addEventListener('animationend', () => { clearTimeout(fallback); el.remove(); }, { once: true });
		} else {
			this._growSpacer(gap);
			el.remove();
		}
	}

	// ── Scroll animation ──────────────────────────────────────────────────────

	_startScrollAnimation() {
		const frame = () => {
			if(!this._animFrameId) return;
			this._animFrameId = requestAnimationFrame(frame);
			this._stepScroll();
		};
		this._animFrameId = requestAnimationFrame(frame);
	}

	// Fraction of the remaining distance covered each frame — the "speed" setting.
	_getScrollSpeed() {
		const speed = Number(this.settings.scrollSpeed);
		if(!(speed > 0)) return 0.2;
		return Math.min(1, speed);
	}

	// Move the scroll offset by a fraction of what is left. The offset itself only ever
	// changes by whole pixels — the sub-pixel remainder is kept here and spent on a later
	// frame, so a slow setting still moves without ever landing the text between pixels.
	_advanceScroll(axis, diff, speed) {
		if(diff < 1) {
			this._scrollCarry = 0;
			return;
		}
		this._scrollCarry += diff * speed;
		const step = Math.trunc(this._scrollCarry);
		if(!step) return;
		this._scrollCarry -= step;
		this.container[axis] += step;
	}

	_stepScroll() {
		const el = this.container;
		if(!this.spacer) return;
		const speed = this._getScrollSpeed();
		const last = el.lastElementChild;
		const hasMessages = last && last !== this.spacer;

		// Decays towards zero at the same rate the scroll catches up, so a removed message's
		// gap closes at the speed everything else moves. Snap the last half pixel shut.
		this._spacerPx -= this._spacerPx * speed;
		if(this._spacerPx < 0.5) this._spacerPx = 0;

		if(this.settings.horizontal) {
			this._applySpacer();
			if(!hasMessages) return;
			const paddingRight = parseInt(getComputedStyle(el).paddingRight) || 0;
			const target = last.offsetLeft + last.offsetWidth + paddingRight - el.clientWidth;
			this._advanceScroll('scrollLeft', target - el.scrollLeft, speed);
		} else {
			const paddingBottom = parseInt(getComputedStyle(el).paddingBottom) || 0;
			// Bottom alignment: while the messages are shorter than the widget, hold the
			// spacer at the leftover height so they sit on the bottom edge. Once they
			// overflow there is no leftover and this is a no-op.
			let floor = 0;
			if(hasMessages && this.settings.alignment === 'bottom') {
				const messagesHeight = last.offsetTop + last.offsetHeight - this.spacer.offsetHeight;
				floor = Math.max(0, el.clientHeight - paddingBottom - messagesHeight);
			}
			this._applySpacer(floor);
			if(!hasMessages) return;
			const target = last.offsetTop + last.offsetHeight + paddingBottom - el.clientHeight;
			this._advanceScroll('scrollTop', target - el.scrollTop, speed);
		}
	}

	// The spacer takes the removed messages' room on whichever axis the chat scrolls;
	// in horizontal mode its height is left to the stylesheet (it doubles as the strut
	// that gives the line box the full widget height).
	//
	// Whole pixels only. The spacer sits in front of every message, so a fractional size
	// puts the whole line on a fractional offset — and since it decays a little each frame,
	// that offset lands somewhere new every frame and the text shimmers as it is
	// re-rasterised. The fractional part lives on in _spacerPx, which is what decays.
	_applySpacer(floor = 0) {
		const px = Math.round(Math.max(this._spacerPx, floor));
		if(this.settings.horizontal) {
			this.spacer.style.width = px + 'px';
			this.spacer.style.height = '';
		} else {
			this.spacer.style.height = px + 'px';
			this.spacer.style.width = '';
		}
	}

	_growSpacer(px) {
		if(!(px > 0)) return;
		this._spacerPx += px;
		this._applySpacer();
	}

	_stopScrollAnimation() {
		if(this._animFrameId) {
			cancelAnimationFrame(this._animFrameId);
			this._animFrameId = null;
		}
	}
}
